import NextImage from 'next/image';
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { Button } from 'shared/components/button';
import { falArrowRight } from 'shared/@generated/icons/falArrowRight';
import type { AdvisorTranslate } from 'shared/library/breed-advisor';

const dogs = [
  { file: 'chihuahua', height: 0.28, width: 559 },
  { file: 'shiba', height: 0.44, width: 606 },
  { file: 'border-collie', height: 0.62, width: 685 },
  { file: 'golden-retriever', height: 0.78, width: 726 },
  { file: 'great-dane', height: 1, width: 653 },
] as const;

const positions = dogs.reduce<number[]>((left, _dog, index) => {
  if (!index) return [0];
  const previous = dogs[index - 1] ?? dogs[0];
  return [...left, (left[index - 1] ?? 0) + (previous.width / 480) * previous.height * 0.55];
}, []);
const chartWidth = (positions[4] ?? 0) + dogs[4].width / 480;

type Edge = 'minimum' | 'maximum';
type Drag = { pointerId: number; edge: Edge; singleSize: number | null; offset: number };

export function BreedAdvisorSizeInput({
  copy,
  disabled,
  onSubmit,
}: {
  copy: AdvisorTranslate;
  disabled?: boolean;
  onSubmit: (min: number, max: number) => void;
}) {
  const [[minimum, maximum], setRange] = useState<[number, number]>([1, 5]);
  const range = useRef<[number, number]>([1, 5]);
  const rail = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);

  function finishDrag() {
    const current = drag.current;
    drag.current = null;
    if (current && rail.current?.hasPointerCapture(current.pointerId)) {
      rail.current.releasePointerCapture(current.pointerId);
    }
  }

  useEffect(() => {
    if (disabled) finishDrag();
    return finishDrag;
  }, [disabled]);

  function update(value: number, edge: Edge) {
    if (disabled) return;
    const [from, to] = range.current;
    const bounded = Math.min(5, Math.max(1, value));
    const next: [number, number] =
      edge === 'minimum' ? [Math.min(bounded, to), to] : [from, Math.max(bounded, from)];
    range.current = next;
    setRange(next);
  }

  function valueAt(event: PointerEvent<HTMLDivElement>) {
    const box = event.currentTarget.getBoundingClientRect();
    const position =
      (event.clientX - box.left - 24 - (drag.current?.offset ?? 0)) / Math.max(1, box.width - 48);
    return Math.min(5, Math.max(1, Math.round(position * 4) + 1));
  }

  function movePointer(event: PointerEvent<HTMLDivElement>) {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId || disabled) return;
    const value = valueAt(event);
    // When both handles meet, either side can still be expanded by dragging away.
    if (current.singleSize !== null && value !== current.singleSize) {
      current.edge = value < current.singleSize ? 'minimum' : 'maximum';
      current.singleSize = null;
    }
    update(value, current.edge);
  }

  function keyDown(event: KeyboardEvent<HTMLButtonElement>, edge: Edge) {
    if (disabled) return;
    let value = edge === 'minimum' ? minimum : maximum;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') value++;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') value--;
    else if (event.key === 'PageUp') value += 2;
    else if (event.key === 'PageDown') value -= 2;
    else if (event.key === 'Home') value = 1;
    else if (event.key === 'End') value = 5;
    else return;
    event.preventDefault();
    update(value, edge);
  }

  return (
    <div
      data-testid="breed-advisor-size-input"
      className="text-brand-brown-dark flex w-full flex-col gap-2 self-end lg:gap-4"
    >
      <div>
        <div className="relative" aria-hidden="true">
          {[15, 40, 65].map((top) => (
            <span
              key={top}
              className="border-brand-brown-dark/5 pointer-events-none absolute inset-x-0 border-t"
              style={{ top: `${top}%` }}
            />
          ))}
          <span
            className="relative mx-auto block w-3/4 max-w-lg lg:w-full"
            style={{ aspectRatio: `${chartWidth} / 1` }}
          >
            {dogs.map((dog, index) => (
              <NextImage
                key={dog.file}
                src={`/images/breed-advisor/size/${dog.file}.webp`}
                alt=""
                width={dog.width}
                height={480}
                unoptimized
                draggable={false}
                className={`pointer-events-none absolute bottom-0 w-auto max-w-none select-none object-contain object-bottom transition-opacity duration-200 motion-reduce:transition-none ${index + 1 >= minimum && index + 1 <= maximum ? 'opacity-100' : 'opacity-40'}`}
                style={{
                  left: `${((positions[index] ?? 0) / chartWidth) * 100}%`,
                  height: `${dog.height * 100}%`,
                  zIndex: 5 - index,
                }}
              />
            ))}
          </span>
        </div>
        <div
          ref={rail}
          data-testid="breed-advisor-size-range"
          className={`relative h-11 w-full touch-none select-none ${disabled ? 'opacity-50' : ''}`}
          onPointerDown={(event) => {
            if (disabled || event.button !== 0 || drag.current) return;
            const [from, to] = range.current;
            const value = valueAt(event);
            const clickedEdge = (event.target as HTMLElement)
              .closest<HTMLButtonElement>('[data-edge]')
              ?.getAttribute('data-edge');
            const edge =
              clickedEdge === 'minimum' || clickedEdge === 'maximum'
                ? clickedEdge
                : from === to
                  ? value < from
                    ? 'minimum'
                    : 'maximum'
                  : Math.abs(value - from) <= Math.abs(value - to)
                    ? 'minimum'
                    : 'maximum';
            const box = event.currentTarget.getBoundingClientRect();
            const handleValue = edge === 'minimum' ? from : to;
            const handleX = box.left + 24 + ((handleValue - 1) / 4) * (box.width - 48);
            drag.current = {
              pointerId: event.pointerId,
              edge,
              singleSize: from === to ? from : null,
              offset: clickedEdge ? event.clientX - handleX : 0,
            };
            event.currentTarget.setPointerCapture(event.pointerId);
            movePointer(event);
          }}
          onPointerMove={movePointer}
          onPointerUp={(event) => {
            if (drag.current?.pointerId !== event.pointerId) return;
            movePointer(event);
            finishDrag();
          }}
          onPointerCancel={(event) => {
            if (drag.current?.pointerId === event.pointerId) finishDrag();
          }}
          onLostPointerCapture={(event) => {
            if (drag.current?.pointerId === event.pointerId) drag.current = null;
          }}
        >
          <span className="bg-brand-brown-dark/10 absolute inset-x-6 top-1/2 h-1 -translate-y-1/2 rounded-full">
            <span
              className="bg-brand-green-dark absolute h-full rounded-full"
              style={{ left: `${(minimum - 1) * 25}%`, right: `${(5 - maximum) * 25}%` }}
            />
            {[1, 2, 3, 4, 5].map((value) => (
              <span
                key={value}
                className="bg-brand-brown-dark/25 absolute top-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{ left: `${(value - 1) * 25}%` }}
              />
            ))}
            {(['minimum', 'maximum'] as const).map((edge) => {
              const value = edge === 'minimum' ? minimum : maximum;
              return (
                <button
                  key={edge}
                  type="button"
                  role="slider"
                  aria-label={copy(`questions.size.${edge === 'minimum' ? 'from' : 'to'}`)}
                  aria-valuemin={edge === 'minimum' ? 1 : minimum}
                  aria-valuemax={edge === 'minimum' ? maximum : 5}
                  aria-valuenow={value}
                  aria-valuetext={copy(`questions.size.sizes.${value}`)}
                  aria-orientation="horizontal"
                  disabled={disabled}
                  data-edge={edge}
                  onKeyDown={(event) => keyDown(event, edge)}
                  className="group absolute top-1/2 z-10 flex size-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center bg-transparent outline-none disabled:cursor-not-allowed"
                  style={{ left: `${(value - 1) * 25}%` }}
                >
                  <span className="border-brand-green-dark group-focus-visible:ring-brand-green/30 flex size-6 items-center justify-center rounded border-2 bg-white group-focus-visible:ring-4">
                    <span className="bg-brand-green-dark h-2 w-px" />
                  </span>
                </button>
              );
            })}
          </span>
        </div>
        <div className="text-brand-brown-dark/60 grid grid-cols-3 items-center gap-2 text-xs">
          <span>
            <span className="block">{copy('questions.size.from')}</span>
            <strong className="text-brand-brown-dark font-semibold">
              {copy(`questions.size.sizes.${minimum}`)}
            </strong>
          </span>
          <span className="text-2xs text-center">{copy('questions.size.drag_hint')}</span>
          <span className="text-right">
            <span className="block">{copy('questions.size.to')}</span>
            <strong className="text-brand-brown-dark font-semibold">
              {copy(`questions.size.sizes.${maximum}`)}
            </strong>
          </span>
        </div>
      </div>
      <Button
        text={copy('questions.size.continue')}
        icon={falArrowRight}
        color="white"
        dense
        disabled={disabled}
        className="self-end"
        textClassName="text-black"
        testID="breed-advisor-size-continue"
        onPress={() => {
          if (!disabled) onSubmit(minimum, maximum);
        }}
      />
    </div>
  );
}
